<script setup lang="ts">
import {ref, onMounted, nextTick, computed, watchEffect} from "vue";
import type { Component } from "../types.ts";
import {parsePx} from "../utils/ElementUtils.ts";

const props = defineProps<{
  item: Component
}>();

const container = ref<HTMLElement | null>(null);

const containerSize = ref(0);

const tp = computed(() => ({
  scroll_speed: props.item?.ticker_props?.scroll_speed ?? 200,
  pixels_shift: props.item?.ticker_props?.pixels_shift ?? 0,
  queue_child: props.item?.ticker_props?.queue_child ?? false,
  vertical: props.item?.ticker_props?.vertical ?? false,
  wait_time: props.item?.ticker_props?.wait_time ?? 0,
  start_empty: props.item?.ticker_props?.start_empty ?? false
}));

function measureFromDom() {
  const c = container.value;
  if (!c) return;

  // Keep measurement simple and stable (no observers).
  const cRect = c.getBoundingClientRect();
  containerSize.value = tp.value.vertical ? cRect.height : cRect.width;
}

onMounted(async () => {
  await nextTick();
  measureFromDom();
});

watchEffect(() => {
  // see docs/EXTRACTED_COMMENTS.md#c049
  void nextTick().then(() => {
    measureFromDom();
  });
});

const styledUnitSize = computed(() => {
  const children = props.item?.children ?? [];
  if (children.length === 0) return 0;

  const size = Math.max(
      ...children.map((el) =>
          tp.value.vertical
              ? parsePx((el as any)?.props?.style?.height) / props.item.scale
              : parsePx((el as any)?.props?.style?.width) / props.item.scale
      )
  );
  return Number.isFinite(size) ? size : 0;
});

const unitSize = computed(() => styledUnitSize.value);

const unitRepeatCount = computed(() => {
  // see docs/EXTRACTED_COMMENTS.md#c050
  const u = unitSize.value;
  const cont = containerSize.value;
  if (u <= 0 || cont <= 0) return 2;

  const unitWithGap = u + Math.max(0, tp.value.pixels_shift);
  const safeUnit = Math.max(1, unitWithGap);

  // Clamp to avoid pathological huge groups when measurements are off.
  return Math.min(50, Math.max(2, Math.ceil(cont / safeUnit) + 1));
});

const groupTravel = computed(() => {
  const u = unitSize.value;
  const n = unitRepeatCount.value;
  if (u <= 0 || n <= 0) return 0;

  // group content length + trailing gap between groups
  const gap = Math.max(0, tp.value.pixels_shift);
  const content = n * u + Math.max(0, n - 1) * gap;
  return content + gap;
});

const trackStyle = computed(() => {
  const distance = tp.value.start_empty
      ? containerSize.value + unitSize.value
      : groupTravel.value;
  const speed = Math.max(1, tp.value.scroll_speed);
  const duration = distance > 0 ? distance / speed : 1;
  const delay = tp.value.wait_time;

  return {
    "--unit-size": `${unitSize.value}px`,
    "--group-size": `${groupTravel.value}px`,
    "--container-size": `${containerSize.value}px`,
    "--pixels-shift": `${tp.value.pixels_shift}px`,
    "--ticker-duration": `${Math.max(0.5, duration)}s`,
    "--ticker-delay": `${delay}s`
  } as Record<string, string>;
});
</script>

<template>
  <div v-bind="item.props" class="ticker" ref="container">
    <div class="ticker__mask">
      <div
          class="ticker__track"
          :style="trackStyle"
          :class="{
          'ticker__track--vertical': tp.vertical,
          'ticker__track--horizontal': !tp.vertical,
          'ticker__track--start-empty': tp.start_empty,
          'ticker__track--queue': tp.queue_child
        }"
      >
        <div class="ticker__group">
          <div class="ticker__unit">
            <div v-if="item?.published?.src" class="ticker__html" v-html="item.published.src"></div>
            <slot />
          </div>

          <div
            v-for="idx in (unitRepeatCount - 1)"
            :key="`u1-${idx}`"
            class="ticker__unit"
            aria-hidden="true"
            :data-repeat-idx="idx"
          >
            <div v-if="item?.published?.src" class="ticker__html" v-html="item.published.src"></div>
            <slot />
          </div>
        </div>

        <div class="ticker__group" aria-hidden="true">
          <div class="ticker__unit">
            <div v-if="item?.published?.src" class="ticker__html" v-html="item.published.src"></div>
            <slot />
          </div>

          <div
            v-for="id in (unitRepeatCount - 1)"
            :key="`u2-${id}`"
            class="ticker__unit"
            aria-hidden="true"
            :data-repeat-idx="id"
          >
            <div v-if="item?.published?.src" class="ticker__html" v-html="item.published.src"></div>
            <slot />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ticker {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
}

.ticker__mask {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.ticker__track {
  --ticker-start: 0px;
  display: inline-flex;
  align-items: center;
  will-change: transform;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  animation-delay: var(--ticker-delay, 0s);
}

.ticker__track--start-empty {
  --ticker-start: var(--container-size, 100%);
}

.ticker__track--horizontal {
  flex-direction: row;
  animation-name: ticker-left;
  animation-duration: var(--ticker-duration, 10s);
}

.ticker__track--vertical {
  flex-direction: column;
  animation-name: ticker-up;
  animation-duration: var(--ticker-duration, 10s);
}

.ticker__group {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: var(--pixels-shift, 0px);
}

.ticker__track--horizontal .ticker__group {
  margin-right: var(--pixels-shift, 0px);
}

.ticker__track--vertical .ticker__group {
  margin-bottom: var(--pixels-shift, 0px);
}

.ticker__track--horizontal .ticker__group {
  flex-direction: row;
}

.ticker__track--vertical .ticker__group {
  flex-direction: column;
}

.ticker__unit {
  flex: 0 0 auto;
  position: relative;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  gap: 2rem;
}

.ticker__track--horizontal .ticker__unit {
  width: var(--unit-size, auto);
}

.ticker__track--vertical .ticker__unit {
  height: var(--unit-size, auto);
}

.ticker__html > * {
  display: inline-block;
  margin-right: 2rem;
}

/* keyframes */
@keyframes ticker-left {
  to {
    transform: translateX(calc(-1 * var(--group-size, 0px)));
  }
  from {
    transform: translateX(var(--ticker-start, 0px));
  }
}

@keyframes ticker-up {
  to {
    transform: translateY(calc(-1 * var(--group-size, 0px)));
  }
  from {
    transform: translateY(var(--ticker-start, 0px));
  }
}
</style>
